/**
 * Pedidos de compra.
 *
 * Lo que interesa mirar cada mañana son los **retrasados**: material
 * pedido que no ha llegado y que ya está comprometido para una orden de
 * trabajo con fecha.
 */

import { useSearchParams } from 'react-router-dom';
import { DataTable, type Column } from '../table/data-table.js';
import { useListing } from '../table/use-listing.js';
import { PurchasingActions } from './purchasing-actions.js';
import { money, day } from '../table/format.js';

interface Row {
  id: string;
  number: string;
  status: string;
  ordered_on: string;
  expected_on: string | null;
  supplier_name: string;
  supplier_code: string;
  total: string;
  currency: string;
  approved_at: string | null;
  days_late: number | null;
}


const COLUMNS: Column<Row>[] = [
  { id: 'number', label: 'Nº', render: (r) => r.number, sortable: true, width: '10rem' },
  { id: 'supplierName', label: 'Proveedor', render: (r) => r.supplier_name },
  { id: 'status', label: 'Estado', render: (r) => r.status, width: '9rem' },
  { id: 'orderedOn', label: 'Pedido', render: (r) => day(r.ordered_on), sortable: true, width: '8rem' },
  {
    id: 'expectedOn',
    label: 'Prevista',
    width: '12rem',
    render: (r) => {
      if (r.expected_on === null) return <span className="due--none">—</span>;
      if ((r.days_late ?? 0) > 0) {
        return (
          <span className="due due--late">
            ⚠ {r.days_late} d · {r.expected_on}
          </span>
        );
      }
      return <span className="due">{r.expected_on}</span>;
    },
    exportValue: (r) => r.expected_on ?? '',
  },
  {
    id: 'approvedAt',
    label: 'Aprobación',
    width: '10rem',
    render: (r) =>
      r.approved_at === null ? (
        <span className="pill pill--warn">⚠ Pendiente</span>
      ) : (
        <span className="pill pill--ok">✓ Aprobado</span>
      ),
    exportValue: (r) => (r.approved_at === null ? 'Pendiente' : 'Aprobado'),
  },
  {
    id: 'total',
    label: 'Total',
    numeric: true,
    render: (r) => money(r.total, r.currency),
    exportValue: (r) => r.total,
  },
];

const COLUMN_IDS = COLUMNS.map((c) => c.id);

const QUICK = [
  { id: 'all', label: 'Todos', param: {} },
  { id: 'late', label: 'Retrasados', param: { late: 'true' } },
  { id: 'approval', label: 'Sin aprobar', param: { pendingApproval: 'true' } },
] as const;

export function PurchaseOrdersScreen(): React.ReactNode {
  const [params] = useSearchParams();
  const quick = QUICK.find((q) => q.id === params.get('v')) ?? QUICK[0];

  const listing = useListing<Row>({
    path: '/purchase-orders',
    columnIds: COLUMN_IDS,
    extraParams: quick.param,
    preserve: ['v'],
  });

  return (
    <section className="screen">
      <header className="screen__head">
        <h1 className="screen__title">Compras</h1>
        <div className="chips" role="group" aria-label="Filtros rápidos">
          {QUICK.map((option) => (
            <button
              key={option.id}
              type="button"
              className={`chip${quick.id === option.id ? ' chip--on' : ''}`}
              aria-pressed={quick.id === option.id}
              onClick={() => listing.setParam('v', option.id === 'all' ? null : option.id)}
            >
              {option.label}
            </button>
          ))}
        </div>
        {listing.total !== null && <span className="screen__count">{listing.total} pedidos</span>}
      </header>

      {listing.error !== null && (
        <p className="screen__error" role="alert">
          {listing.error}
        </p>
      )}

      <DataTable
        columns={COLUMNS}
        rows={listing.rows}
        idOf={(r) => r.id}
        state={listing.state}
        onStateChange={listing.setState}
        exportName="pedidos-de-compra"
        loading={listing.loading}
        hasMore={listing.hasMore}
        onLoadMore={listing.loadMore}
        totalMatching={listing.total}
        emptyMessage="No hay pedidos que coincidan."
      />

      <PurchasingActions onDone={() => listing.reload()} />
    </section>
  );
}
